import {
  NavigationProvider,
  NavigationProviderCommands,
  RouteType,
  INavigationItem
} from '@nrwl/navigation-abstract';
import {
  Router,
  ActivatedRoute,
  NavigationExtras,
  NavigationEnd,
  ActivatedRouteSnapshot
} from '@angular/router';
import { LocationParser } from './location.parser';
import { Injectable } from '@angular/core';
import { filter } from 'rxjs/operators';
import { RootParametersProvider } from '@nrwl/common-lib';

const RESET_FF = {
  fuzzy_filter: JSON.stringify({ expression: [] }),
  frozen_fuzzy_filter: null,
  testId: null,
  testDeleted: null,
  activetest_clicked: null,
  layeredSource: null,
  isFilterApplied: null,
  test_list_filter: null
};

/**
 * An URL structure under PTC product looks like this:
 * http://localhost:4200/#/perspective/dfvhu/(dashboard:multi-hop/jobs//page:yuidsa//wizard:add_multihop_job)#dashboard
 * Properties:
 * - It starts with perspective/:id segment
 * - Then, one or more named outlets are referenced (dashboard/page/wizard).
 * - Finally, layout needs a tip, which of those outlets must be emphasized (eg focused) right now.
 *   This is done via URL Fragment (#dashboard).
 * - Each named outlet (eg, dashboard) may have nested routes. Example: dashboard/multi-hop/jobs.
 *   These subroutes may have parameters (eg, /jobs/:jobId)
 */
@Injectable({
  providedIn: 'root'
})
export class FusionUINavigationProvider extends NavigationProvider {
  currentPerspectiveId: string;

  constructor(
    private readonly router: Router,
    private readonly activeRoute: ActivatedRoute,
    readonly rootParams: RootParametersProvider
  ) {
    super();
    this.registerListeners();
  }

  registerListeners(): void {
    this.router.events.pipe(filter(event => event instanceof NavigationEnd)).subscribe(() => {
      try {
        this.determinePerspectiveId(this.activeRoute.snapshot);
      } catch (error) {
        // TODO: we need to identify cases when this error is irrelevant (eg, first time loading the app)
        // all other cases should be properly reported to console.
      }
    });
  }

  determinePerspectiveId(snapshot: ActivatedRouteSnapshot): void {
    const visitor = new LocationParser();
    const newOpenOutlets = visitor.start(snapshot);
    this.currentPerspectiveId = newOpenOutlets.perspectiveId;
  }

  // eslint-disable-next-line
  navigatePerspective(id: any): NavigationProviderCommands {
    // simply memorize the next perspective ID - this will be used by subsequent navigation command.
    this.currentPerspectiveId = id;
    return this;
  }

  // eslint-disable-next-line
  addRootParam(topic: string, value: any): NavigationProviderCommands {
    if (!topic) {
      // no parameter is supplied, do not bother to create wrapper
      return this;
    }
    const result = new NavigationState(this);
    result.routeParams[topic] = value;
    return result;
  }

  // do not call like this: this.navigator.navigateToPage({page: ["pageName"]}, ..);
  // call like this:        this.navigator.navigateToPage(["pageName"], ...);
  //
  // OLPT-7502 - Treat auxillery routes to page and dashboard as equals. If one loads, the other
  // one will unload. The difference between the 2 routes? 'dashboard' is more like a
  // traditional component page load while pages loaded under route 'page' uses the
  // perspective.layout component
  // eslint-disable-next-line
  navigateToPage(pageOutletParams: any, navigationExtras?: NavigationExtras): void {
    const extras: NavigationExtras = {
      fragment: 'page',
      ...navigationExtras
    };
    if (!extras.queryParamsHandling) {
      extras.queryParamsHandling = 'merge';
    }

    this.router.navigate(
      [
        '/perspective',
        this.currentPerspectiveId,
        {
          outlets: {
            page: pageOutletParams,
            dashboard: null
          }
        }
      ],
      extras
    );
  }

  /**
   * The framework has adopted a common 'practise' to use parameter 'defaultParams' as
   * a way to pass params to wizard to be launched.
   * This makes it feasible to 'clean' the parameter automatically from the URL, whenever we
   * navigate away from wizards.
   */
  // eslint-disable-next-line
  navigateToWizard(wizardOutletParams: any, navigationExtras?: NavigationExtras): void {
    const extras: NavigationExtras = {
      preserveFragment: true,
      replaceUrl: true,
      ...navigationExtras
    };
    if (!extras.queryParamsHandling) {
      extras.queryParamsHandling = 'merge';
    }
    // take care to wipe out this param from URL
    if (!wizardOutletParams) {
      if (extras.queryParams) {
        extras.queryParams['defaultParams'] = null;
      } else {
        extras.queryParams = {
          defaultParams: null
        };
      }
    }

    this.router.navigate(
      [
        '/perspective',
        this.currentPerspectiveId,
        {
          outlets: {
            wizard: wizardOutletParams,
            testSetup: null
          }
        }
      ],
      extras
    );
  }

  //eslint-disable-next-line
  navigateToTestSetup(testPageOutletParams: any, navigationExtras?: NavigationExtras): void {
    const extras: NavigationExtras = {
      preserveFragment: true,
      replaceUrl: true,
      ...navigationExtras
    };
    if (!extras.queryParamsHandling && extras.queryParamsHandling !== '') {
      extras.queryParamsHandling = 'merge';
    }
    // take care to wipe out this param from URL
    if (!testPageOutletParams) {
      if (extras.queryParams) {
        extras.queryParams['defaultParams'] = null;
      } else {
        extras.queryParams = {
          defaultParams: null
        };
      }
    }
    this.router.navigate(
      [
        '/perspective',
        this.currentPerspectiveId,
        {
          outlets: {
            testSetup: testPageOutletParams,
            wizard: null
          }
        }
      ],
      extras
    );
  }

  // eslint-disable-next-line
  navigateToDashboard(dashboardOutletParams: any, navigationExtras?: NavigationExtras): void {
    const extras: NavigationExtras = {
      fragment: 'dashboard',
      ...navigationExtras
    };
    if (!extras.queryParamsHandling) {
      extras.queryParamsHandling = 'merge';
    }

    const outletParams = {
      dashboard: dashboardOutletParams,
      page: null
    };
    if (extras?.queryParams?.hideTestSetup) {
      outletParams['testSetup'] = null;
    }
    this.router.navigate(
      [
        '/perspective',
        this.currentPerspectiveId,
        {
          outlets: outletParams
        }
      ],
      extras
    );
  }

  /**
   * Navigate using router by Type
   * @param item: INavigationItem - Information describing the route to navigate to
   */
  public navigateByType(item: INavigationItem): void {
    const qparams = {
      ...RESET_FF,
      ...item.queryParams
    };
    this.router.navigate(['/' + item.type, item.typeId], {
      queryParams: qparams,
      queryParamsHandling: 'merge'
    });
  }

  /**
   * Navigate using the router by Outlet
   * @param item: INavigationItem - Information describing the route to navigate to
   */
  public navigateByOutletParams(item: INavigationItem): void {
    const outletParams = {};

    outletParams[item.outlet] = Array.isArray(item.navigation)
      ? item.navigation
      : [item.navigation];

    switch (item.outlet) {
      case 'page':
        outletParams['dashboard'] = null;
        break;
      case 'dashboard':
        outletParams['page'] = null;
        break;
    }

    const qparams = {
      ...RESET_FF,
      ...item.queryParams
    };

    this.router.navigate([`/${item.type}`, item.typeId, { outlets: outletParams }], {
      fragment: `${this._determineActiveOutlet(item)}`,
      queryParams: qparams,
      queryParamsHandling: 'merge'
    });
  }

  removeQueryParams(routeType: RouteType, routeName: string, queryNames: string[]): void {
    const queryParams = {};
    queryNames.forEach(qN => (queryParams[qN] = null));
    switch (routeType) {
      case RouteType.PAGE:
        this.navigateToPage([routeName], { queryParams: queryParams });
        break;
      case RouteType.DASHBOARD:
        this.navigateToDashboard([routeName], { queryParams: queryParams });
        break;
      case RouteType.WIZARD:
        this.navigateToWizard([routeName], { queryParams: queryParams });
        break;
      default:
        break;
    }
  }

  private _determineActiveOutlet(item: INavigationItem) {
    if (item.type === 'perspective') {
      return item.outlet ? item.outlet : 'page';
    }
    if (item.type === 'wizards') {
      return 'wizards';
    }

    if (item.type === 'testSetup') {
      return 'testSetup';
    }
    // unknown. default to page
    return 'page';
  }
}

class NavigationState implements NavigationProviderCommands {
  routeParams: any = {}; // eslint-disable-line

  constructor(readonly boundService: FusionUINavigationProvider) {}

  // eslint-disable-next-line
  navigatePerspective(id: any): NavigationProviderCommands {
    // no use case yet, thus not implemented.
    if (Object.keys(this.routeParams).length > 0) {
      throw Error('NavigationState does not support perspective navigation!');
    }
    return this.boundService.navigatePerspective(id);
  }

  // eslint-disable-next-line
  navigateToPage(pageOutletParams: any, navigationExtras?: NavigationExtras) {
    const optionalQueryParams = this._emitRootParameters(this.routeParams);
    const finalNavExtras: NavigationExtras = { ...navigationExtras };
    finalNavExtras.queryParams = this._mergeQueryParams(
      finalNavExtras.queryParams,
      optionalQueryParams
    );

    this.boundService.navigateToPage(pageOutletParams, finalNavExtras);
  }

  // eslint-disable-next-line
  navigateToWizard(wizardOutletParams: any, navigationExtras?: NavigationExtras) {
    const optionalQueryParams = this._emitRootParameters(this.routeParams);
    const finalNavExtras: NavigationExtras = { ...navigationExtras };
    finalNavExtras.queryParams = this._mergeQueryParams(
      finalNavExtras.queryParams,
      optionalQueryParams
    );

    this.boundService.navigateToWizard(wizardOutletParams, finalNavExtras);
  }

  // eslint-disable-next-line
  navigateToTestSetup(setupOutletParams: any, navigationExtras?: NavigationExtras) {
    const optionalQueryParams = this._emitRootParameters(this.routeParams);
    const finalNavExtras: NavigationExtras = { ...navigationExtras };
    finalNavExtras.queryParams = this._mergeQueryParams(
      finalNavExtras.queryParams,
      optionalQueryParams
    );

    this.boundService.navigateToTestSetup(setupOutletParams, finalNavExtras);
  }

  // eslint-disable-next-line
  navigateToDashboard(dashboardOutletParams: any, navigationExtras?: NavigationExtras) {
    const optionalQueryParams = this._emitRootParameters(this.routeParams);
    const finalNavExtras: NavigationExtras = { ...navigationExtras };
    finalNavExtras.queryParams = this._mergeQueryParams(
      finalNavExtras.queryParams,
      optionalQueryParams
    );

    this.boundService.navigateToDashboard(dashboardOutletParams, finalNavExtras);
  }

  // eslint-disable-next-line
  addRootParam(topic: string, value: any): NavigationProviderCommands {
    // having multiple root params is not supported (but could be done if desired)
    throw new Error('NavigationState does not support nesting!');
  }

  _mergeQueryParams(outerQueryParams, innerQueryParams) {
    const result = { ...outerQueryParams, ...innerQueryParams };
    return result;
  }

  // eslint-disable-next-line
  _emitRootParameters(whatParams): any {
    let output = {};
    for (const topic of Object.keys(whatParams)) {
      const value = whatParams[topic];
      const optionalParam = this.boundService.rootParams.notifyListenersBeforeRouteChange(
        topic,
        value
      );
      output = this._mergeQueryParams(output, optionalParam);
    }
    return output;
  }
}
